import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import i18n from '@/lib/i18n/i18n';
import ReactCountryFlag from 'react-country-flag';
import { getCountryCallingCode, getCountries } from 'libphonenumber-js';

interface CountryOption {
    value: string;
    label: JSX.Element;
    phoneCode: string;
}

interface PhoneInputProps {
    name: string;
    placeholder: string;
    label: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    type: string;
    error?: string;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
    name,
    placeholder,
    label,
    value,
    onChange,
    type,
    error,
}) => {
    const isRtl = i18n.dir() === 'rtl';

    // Detect Dark Mode
    const [isDark, setIsDark] = useState(
        document.documentElement.classList.contains('dark'),
    );

    // Watch for Dark Mode changes
    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDark(
                document.documentElement.classList.contains('dark'),
            );
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        });

        return () => observer.disconnect();
    }, []);

    // Get list of country codes
    const countries: CountryOption[] = getCountries().map((country) => ({
        value: country,
        label: (
            <div className="flex items-center">
                <ReactCountryFlag
                    countryCode={country}
                    svg
                    className="w-6 h-6"
                />
            </div>
        ),
        phoneCode: `+${getCountryCallingCode(country)}`,
    }));

    // Default to Saudi Arabia (SA)
    const [selectedCountry, setSelectedCountry] =
        useState<CountryOption>(
            countries.find((c) => c.value === 'SA') || countries[0],
        );

    // Handle country selection
    const handleCountryChange = (
        selectedOption: CountryOption | null,
    ) => {
        if (selectedOption) {
            setSelectedCountry(selectedOption);

            onChange({
                target: {
                    name,
                    value: selectedOption.phoneCode,
                },
            } as React.ChangeEvent<HTMLInputElement>);
        }
    };

    // Handle phone number input
    const handlePhoneChange = (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        let inputValue = e.target.value;

        if (!inputValue.startsWith(selectedCountry.phoneCode)) {
            inputValue = `${selectedCountry.phoneCode}${inputValue.replace(
                /^\+\d+/,
                '',
            )}`;
        }

        onChange({
            target: {
                name,
                value: inputValue,
            },
        } as React.ChangeEvent<HTMLInputElement>);
    };

    return (
        <div className="flex flex-col gap-2">
            <label className="text-base font-medium text-primery-blue-900 dark:text-white">
                {label}
            </label>

            <div className="flex flex-row-reverse w-full">
                <Select
                    options={countries}
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    className={`w-[15%] text-base ${
                        isRtl
                            ? 'border-l-0 rounded-r-lg'
                            : 'border-r-0 rounded-l-lg'
                    }`}
                    classNamePrefix="select"
                    isSearchable
                    styles={{
                        // Main select control
                        control: (base, state) => ({
                            ...base,

                            minHeight: '40px',

                            display: 'flex',
                            alignItems: 'center',

                            borderRadius: isRtl
                                ? '8px 0 0 8px'
                                : '0 8px 8px 0',

                            borderLeft: isRtl
                                ? 'none'
                                : undefined,

                            borderRight: isRtl
                                ? undefined
                                : 'none',

                            backgroundColor: isDark
                                ? '#0D0D0D'
                                : '#FFFFFF',

                            borderColor: state.isFocused
                                ? '#3B82F6'
                                : isDark
                                ? '#262626'
                                : '#D1D5DB',

                            boxShadow: state.isFocused
                                ? '0 0 0 2px rgba(59, 130, 246, 0.2)'
                                : 'none',

                            '&:hover': {
                                borderColor: state.isFocused
                                    ? '#3B82F6'
                                    : isDark
                                    ? '#404040'
                                    : '#9CA3AF',
                            },
                        }),

                        // Selected country
                        singleValue: (base) => ({
                            ...base,

                            display: 'flex',
                            alignItems: 'center',

                            color: isDark
                                ? '#FFFFFF'
                                : '#111827',
                        }),

                        // Dropdown menu
                        menu: (base) => ({
                            ...base,

                            marginTop: '4px',

                            backgroundColor: isDark
                                ? '#0D0D0D'
                                : '#FFFFFF',

                            border: isDark
                                ? '1px solid #262626'
                                : '1px solid #D1D5DB',

                            borderRadius: '8px',

                            overflow: 'hidden',

                            zIndex: 9999,

                            boxShadow: isDark
                                ? '0 10px 25px rgba(0, 0, 0, 0.5)'
                                : '0 10px 25px rgba(0, 0, 0, 0.1)',
                        }),

                        // Dropdown options
                        option: (base, state) => ({
                            ...base,

                            display: 'flex',
                            alignItems: 'center',

                            padding: '8px',

                            cursor: 'pointer',

                            backgroundColor: state.isSelected
                                ? '#2563EB'
                                : state.isFocused
                                ? isDark
                                    ? '#262626'
                                    : '#F3F4F6'
                                : isDark
                                ? '#0D0D0D'
                                : '#FFFFFF',

                            color: state.isSelected
                                ? '#FFFFFF'
                                : isDark
                                ? '#FFFFFF'
                                : '#111827',

                            '&:active': {
                                backgroundColor: '#2563EB',
                                color: '#FFFFFF',
                            },
                        }),

                        // Dropdown list
                        menuList: (base) => ({
                            ...base,

                            padding: '4px',

                            backgroundColor: isDark
                                ? '#0D0D0D'
                                : '#FFFFFF',

                            scrollbarWidth: 'thin',
                        }),

                        // Search input
                        input: (base) => ({
                            ...base,

                            color: isDark
                                ? '#FFFFFF'
                                : '#111827',
                        }),

                        // Placeholder
                        placeholder: (base) => ({
                            ...base,

                            color: isDark
                                ? '#9CA3AF'
                                : '#6B7280',
                        }),

                        // Dropdown arrow
                        dropdownIndicator: (base, state) => ({
                            ...base,

                            color: isDark
                                ? '#9CA3AF'
                                : '#6B7280',

                            transition: 'color 0.2s',

                            '&:hover': {
                                color: isDark
                                    ? '#FFFFFF'
                                    : '#111827',
                            },
                        }),

                        // Clear button
                        clearIndicator: (base) => ({
                            ...base,

                            color: isDark
                                ? '#9CA3AF'
                                : '#6B7280',

                            '&:hover': {
                                color: isDark
                                    ? '#FFFFFF'
                                    : '#111827',
                            },
                        }),

                        // Separator
                        indicatorSeparator: (base) => ({
                            ...base,

                            backgroundColor: isDark
                                ? '#262626'
                                : '#D1D5DB',
                        }),
                    }}
                />

                <input
                    type={type}
                    name={name}
                    placeholder={placeholder}
                    value={value}
                    onChange={handlePhoneChange}
                    className={`flex-1 text-base border focus:outline-none w-[85%] pl-2 text-left
                        bg-white dark:bg-[#0D0D0D]
                        text-gray-900 dark:text-[#86A4C0]
                        placeholder-gray-400 dark:placeholder-gray-500
                        ${
                            isRtl
                                ? 'rounded-r-lg'
                                : 'rounded-l-lg'
                        }
                        ${
                            error
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                                : 'border-gray-300 dark:border-[#262626] focus:border-blue-500 focus:ring-blue-200'
                        }
                        focus:ring-2 transition-colors`}
                    dir="ltr"
                />
            </div>

            {error && (
                <p className="text-sm text-red-600 text-right">
                    {error}
                </p>
            )}
        </div>
    );
};

export default PhoneInput;

